import { Outlet, useLocation } from 'react-router-dom';
import { Box } from '@material-ui/core';
import { useEffect, useMemo } from 'react';
import { IntercomProvider } from 'react-use-intercom';
import { AuxProps } from '@setvi/shared/interfaces';
import DialogProvider from '@setvi/shared/providers/dialog';
import { ROUTES } from 'enumsV2';

// import { ComposeEmailProvider } from 'Providers/ComposeEmail';
import { NylasProvider } from 'Providers/Nylas';
import { UserInfoDialogProvider } from 'Providers/UserInfoDialogProvider';
import { ComposeEmailProvider, UploadResourcePanelProvider } from 'providersV2';
import { FeatureBanner } from 'componentsV2/feature-banner';
import { useBannerNotifications } from 'hooksv2';

import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { useSidebar } from './Sidebar/useSidebar';
import { getPageTitle } from './utils/pageTitle';
import { useStyles } from './style';

const MainContainer = ({ children }: AuxProps) => (
  <NylasProvider>
    <IntercomProvider appId={process.env.INTERCOM_APP_ID}>
      <UploadResourcePanelProvider>
        <DialogProvider>
          <ComposeEmailProvider>
            <UserInfoDialogProvider>{children}</UserInfoDialogProvider>
          </ComposeEmailProvider>
        </DialogProvider>
      </UploadResourcePanelProvider>
    </IntercomProvider>
  </NylasProvider>
);

export const Main = () => {
  const classes = useStyles();
  const location = useLocation();
  const { expandedSidebarMenuItem, layout, handleSetExpandedMenu } =
    useSidebar();

  // Dismissal is persisted server-side (optimistic removal + rollback inside
  // the hook), so the query result is already the source of truth for what to
  // show — no client-side dismissal filtering needed.
  const { notifications, dismiss } = useBannerNotifications();

  useEffect(() => {
    document.title = getPageTitle(location.pathname);
  }, [location]);

  const showTopbar = true;

  const newDesign = useMemo(
    () =>
      [
        ROUTES.PRODUCTS,
        ROUTES.RESOURCES,
        ROUTES.WORKSPACES_FILES,
        ROUTES.AUTO_RFQ
      ].some(route => location.pathname.includes(route)),
    [location.pathname]
  );

  if (!layout)
    return (
      <MainContainer>
        <div className={classes.root}>
          <Box className={classes.pageContainer}>
            <Outlet />
          </Box>
        </div>
      </MainContainer>
    );

  return (
    <MainContainer>
      <div className={classes.wrapper}>
        <FeatureBanner notifications={notifications} onDismiss={dismiss} />
        {showTopbar && <Topbar />}
        <div className={classes.root}>
          <Sidebar
            expandedSidebarMenuItem={expandedSidebarMenuItem}
            setExpandedSidebarMenuItem={handleSetExpandedMenu}
            newDesign={newDesign}
            collapsedDrawer
          />
          <main
            className={classes.container}
            style={{
              backgroundColor: newDesign ? '#f8f9fa' : '#fff'
            }}>
            <Box className={classes.pageContainer}>
              <Outlet />
            </Box>
          </main>
        </div>
      </div>
    </MainContainer>
  );
};
